import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DoCheck,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ButtonComponent, ButtonType } from '@app/components/button';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog';
import {
  InputDirection,
  InputSize,
  InputType,
  InputWidth,
} from '@app/components/input';
import { SecondaryNavigationComponent } from '@app/components/secondary-navigation';
import { SpinnerComponent } from '@app/components/spinner';
import { TextInputComponent } from '@app/components/text-input';
import { ToastService, ToastType } from '@app/components/toast';
import { ViewAlign, ViewComponent, ViewJustify } from '@app/components/view';
import { AdminInviteUserService } from '@app/services/admin-invite-user/admin-invite-user.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { createEmailRegex } from '@app/utils';
import { EnterpriseEntityFeatures } from '@skyfit/core-data-library';

import {
  AdminUserService,
  UserPermission,
} from '@app/services/admin-user/admin-user.service';
import { AcceptedDomainsService } from '@app/utils/accepted-domains/accepted-domains.service';
import { Observable, Subscription } from 'rxjs';
@Component({
  selector: 'app-container-account',
  standalone: true,
  imports: [
    SecondaryNavigationComponent,
    CommonModule,
    RouterModule,
    ViewComponent,
    ButtonComponent,
    SpinnerComponent,
    ConfirmDialogComponent,
    TextInputComponent,
  ],
  templateUrl: './account-container.component.html',
  styleUrl: './account-container.component.css',
})
export class AccountContainerComponent implements DoCheck, OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly acceptedDomainsService = inject(AcceptedDomainsService);
  private readonly translatorService = inject(TranslatorService);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly adminInviteUserService = inject(AdminInviteUserService);
  private readonly toastService = inject(ToastService);
  adminUserService = inject(AdminUserService);
  isLoading = signal(true);
  inviteAdminFirstName = signal('');
  inviteAdminLastName = signal('');
  inviteAdminEmail = signal('');
  inviteDisabled = computed(() => {
    return (
      this.inviteAdminFirstName().length < 2 ||
      this.inviteAdminLastName().length < 2 ||
      !this.emailRegex?.test(this.inviteAdminEmail())
    );
  });
  openAddAdminDialog = signal(false);
  private readonly subscriptions = new Subscription();
  tenant$: Observable<any>;
  currentUrl = '';
  links = [
    { route: '/account', label: 'Account' },
    { route: '/account/manage', label: 'Manage' },
    { route: '/account/resources', label: 'Resources' },
    { route: '/account/support', label: 'Support' }, // support is typically last
  ];
  enterpriseEntityFeatures: EnterpriseEntityFeatures;
  emailRegex: RegExp;
  inviteAdminEmailError = false;
  isEmailRegexWildcard = false;
  acceptedDomains: string;
  emailErrorMessage: string;
  ViewJustify = ViewJustify;
  ViewAlign = ViewAlign;
  ButtonType = ButtonType;
  InputType = InputType;
  InputSize = InputSize;
  InputDirection = InputDirection;
  InputWidth = InputWidth;
  Spacing = Spacing;
  UserPermission = UserPermission;

  handleAddAdminDialog() {
    this.clearAdminUserInfo();
    this.openAddAdminDialog.set(!this.openAddAdminDialog());
  }

  handleInviteAdmin() {
    if (!this.inviteDisabled()) {
      const tenantId = this.adminTenantService.getTenantId();
      if (tenantId) {
        this.subscriptions.add(
          this.adminInviteUserService
            .inviteUser(
              this.inviteAdminEmail(),
              tenantId,
              true,
              this.inviteAdminFirstName(),
              this.inviteAdminLastName()
            )
            .subscribe({
              next: () => {
                this.toastService.add(
                  this.translatorService.translate('account.addedAsAdmin', {
                    firstName: this.inviteAdminFirstName(),
                    lastName: this.inviteAdminLastName(),
                  })
                );
                this.handleAddAdminDialog();
              },
              error: (error) => {
                console.error(error);
                this.toastService.add(
                  this.translatorService.translate('account.errorAddingAdmin', {
                    firstName: this.inviteAdminFirstName(),
                    lastName: this.inviteAdminLastName(),
                  }),
                  ToastType.ERROR
                );
                this.handleAddAdminDialog();
              },
            })
        );
      } else {
        console.error('TenantId Undefined');
        this.toastService.add(
          this.translatorService.translate('account.errorAddingAdmin', {
            firstName: this.inviteAdminFirstName(),
            lastName: this.inviteAdminLastName(),
          }),
          ToastType.ERROR
        );
        this.handleAddAdminDialog();
      }
    }
  }

  ngOnInit() {
    this.tenant$ = this.adminTenantService.tenant$;
    this.currentUrl = this.router.url;
    this.subscriptions.add(
      this.tenant$.subscribe((tenant) => {
        if (tenant) {
          this.enterpriseEntityFeatures = tenant.features;
          this.isLoading.set(false);
          this.acceptedDomains =
            this.acceptedDomainsService.acceptedDomainsText(
              tenant.config?.emailDomains?.value
            );
          this.emailRegex = createEmailRegex(
            tenant.config?.emailDomains?.value
          );

          if (tenant.config?.emailDomains?.value[0] === '.*') {
            this.isEmailRegexWildcard = true;
          }
        }
      })
    );
    this.emailErrorMessage = this.translatorService.translate(
      'account.invalidEmailFormat'
    );
  }

  ngDoCheck() {
    this.currentUrl = this.router.url;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  clearAdminUserInfo(): void {
    this.inviteAdminFirstName.set('');
    this.inviteAdminLastName.set('');
    this.inviteAdminEmail.set('');
  }
}
